import { type WebSection } from '@wbk/contentful/api';

import { Button } from '@wbk/ui';
import { parseSectionItem } from '@wbk/contentful';
import { Link } from '@wbk/config';
import { useAnalytics } from '@wbk/analytics';
import useLocalization from '@/context/localization/useLocalization';
import { useGetEventsList } from '@/hooks/use-get-events';
import { formatCustomDate } from '@/utils';
import { getEventRedirectUrl } from '@/utils/redirect';
import ScrollTitle from '../ui/ScrollTitle';
import EventCard from '../events/EventCard';
import { Carousel } from '../ui/Carousel';

export default function RecommendedEvents({ section }: { section: WebSection }) {
  const { locale } = useLocalization();
  const { trackEvent } = useAnalytics();
  const { data } = useGetEventsList({
    lang: locale,
    limit: 6,
    where: { AND: [{ season: { slug: 'riyadh-season-2025' } }] },
  });

  if (!data?.items.length) {
    return null;
  }

  return (
    <div className='container-padding container'>
      <section className='container px-8 sm:px-12 lg:space-y-12 2xl:px-20'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
          <ScrollTitle title={section.title} />
        </div>
        <Carousel
          containerClassName=''
          options={{
            align: 'center',
          }}
          arrowsClassName='container'
          dots
          dotsClassName=' -bottom-10'
        >
          {data?.items.map((event) => {
            const parsedItem = parseSectionItem({
              item: { ...event, __typename: 'Event' },
              locale,
            });
            if (!parsedItem) return null;
            const parsed = parsedItem;

            const redirectInfo = getEventRedirectUrl(event, event.slug, locale, parsed);

            // @ts-expect-error -- schedule is available but not typed
            const openDate = parsed?.schedule?.openDateTime;
            return (
              <Button
                key={event.id}
                as={Link}
                href={redirectInfo.href}
                theme='white'
                shape='text'
                className='relative shrink-0 basis-60 !p-0 sm:basis-80 ltr:mr-4 rtl:ml-4'
                target={redirectInfo.isExternal ? '_blank' : undefined}
                onClick={() => {
                  trackEvent({
                    event: 'clicked_on_section_item',
                    data: {
                      type: event.__typename,
                      slug: 'slug' in event ? event.slug : '',
                      section: section.id,
                      position: data.items.indexOf(event),
                    },
                  });
                }}
                rel='noreferrer'
              >
                <EventCard
                  venueName={event.location.address || 'Event Name'}
                  eventDate={openDate ? formatCustomDate(new Date(openDate), locale) : ''}
                  priceFrom={event.startingPrice}
                  currency={event.currencyCode}
                  image={event.image11}
                  zoneLogo={event?.zone?.zoneLogo}
                  buttonText={event?.buttonLabel || undefined}
                />
              </Button>
            );
          })}
        </Carousel>
      </section>
    </div>
  );
}
